import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  'A free, copy-paste sponsorship letter template for asking local businesses to back an athlete — what to offer in return, how much to ask for, and how to follow up.';

const FAQS = [
  {
    q: 'How much should I ask a local business to sponsor an athlete?',
    a: 'Start small and specific: $25–$100 is a comfortable, low-pressure ask for most local businesses — a gym, a physical therapist, a family-owned restaurant. A round, modest number is easier to approve on the spot than an open-ended "whatever you can give."',
  },
  {
    q: 'What should I offer a business in return for sponsoring me?',
    a: "A shoutout on your fund page, a mention on social media, or a logo/name credit is usually enough for a small local sponsorship — most businesses are supporting you as a customer or neighbor, not buying ad space. Keep the offer honest and don't overpromise reach you don't have.",
  },
  {
    q: 'Which businesses should I ask first?',
    a: "Places you already have a relationship with: a gym or trainer you use, a family-owned restaurant, a local shop where you're a regular. Warm relationships convert far better than cold ones, and a business owner who already knows you is more likely to say yes on the spot.",
  },
  {
    q: 'Should I ask for a one-time sponsorship or ongoing support?',
    a: "A one-time sponsorship is the easier first ask for a business — it fits their budget as a single line item. Some will offer to sponsor every season if the first one goes well, but don't lead with that; earn it after the first shoutout and photo.",
  },
  {
    q: 'Do I need a physical letter, or is email/text fine?',
    a: "Whatever gets read. A short email or a printed one-page letter dropped off in person both work — in person tends to convert best for local businesses, since it's a real conversation, not just an inbox item. Keep it to one page either way.",
  },
];

const SponsorshipLetterTemplate: React.FC = () => {
  usePageMeta(
    'Sports Sponsorship Letter Template: A Free Letter for Local Businesses',
    ARTICLE_DESCRIPTION,
    {
      article: true,
      image: '/resources/team-huddle.jpg',
      breadcrumbs: [
        { name: 'Resources', path: '/resources' },
        { name: 'Sponsorship Letter Template' },
      ],
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    }
  );
  const TOC_SECTIONS = [
    {
      id: "the-template",
      text: "The template"
    },
    {
      id: "1-ask-for-a-specific-modest-amount",
      text: "1. Ask for a specific, modest amount"
    },
    {
      id: "2-offer-something-real-not-something-inflated",
      text: "2. Offer something real, not something inflated"
    },
    {
      id: "3-start-with-businesses-that-already-know-you",
      text: "3. Start with businesses that already know you"
    },
    {
      id: "4-make-it-easy-to-say-yes-on-the-spot",
      text: "4. Make it easy to say yes on the spot"
    },
    {
      id: "5-follow-through-on-the-exchange",
      text: "5. Follow through on the exchange"
    },
    {
      id: "where-sport-fund-fits",
      text: "Where Sport Fund fits"
    },
    {
      id: "frequently-asked-questions",
      text: "Frequently asked questions"
    },
    {
      id: "read-next",
      text: "Read next"
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        Sports sponsorship letter template: a free letter for local businesses (2026)
      </h1>
      <p className="text-gray-500 mb-8 text-lg">
        A different ask than the one you'd send family — here's a copy-paste letter built for local businesses, plus
        what to offer and how much to ask for.
      </p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />
        <p>
          Asking a business is a different conversation than asking family or friends. A business owner isn't giving
          out of personal affection — they're deciding whether a small, specific ask is worth saying yes to. The
          template below is built around that: a clear amount, a clear exchange, and a reason to say yes fast.
        </p>

        <h2 id="the-template">The template</h2>
        <div className="not-prose bg-gray-50 border border-gray-200 rounded-lg p-6 my-6 whitespace-pre-line text-gray-800">
          {`Hi [Owner/Manager name],

My name is [your name], and I [play/compete in] [sport] at [school/club]. This season I'm reaching out to a few local businesses I trust to ask for a small sponsorship — $[amount] would help cover [registration / travel / gear / tournament fees] for the season.

In exchange, I'd be glad to give [business name] a shoutout on my fundraising page and social media, and add your name or logo to my supporter list. I'm also happy to stop by with a photo once the season's underway.

If this is something you're open to, you can give directly here:

[your Sport Fund link]

Thank you for considering it — win or lose this season, I appreciate you taking the time to read this.

[Your name]
[Your phone number or email]`}
        </div>
        <p>
          Drop it off in person with a printed copy if you can — a short face-to-face conversation converts better
          than an email a busy owner might not open. Bring the link on your phone so they can give on the spot if
          they say yes.
        </p>

        <h2 id="1-ask-for-a-specific-modest-amount">1. Ask for a specific, modest amount</h2>
        <p>
          "Would you like to sponsor me?" is vague and easy to put off. "Would you sponsor me for $50?" is a yes-or-no
          decision a business owner can make in the moment. <strong>$25–$100</strong> is the sweet spot for most
          local businesses — enough to matter, small enough to approve without a second thought.
        </p>

        <h2 id="2-offer-something-real-not-something-inflated">2. Offer something real, not something inflated</h2>
        <p>
          A shoutout on your fund page, a mention in a post, or a logo on your supporter list is a fair, honest
          exchange for a small local sponsorship. Don't promise reach or exposure you don't actually have — most
          businesses are saying yes because they know you, not because they're buying advertising.
        </p>

        <h2 id="3-start-with-businesses-that-already-know-you">3. Start with businesses that already know you</h2>
        <p>
          Your gym, your physical therapist, the restaurant your family orders from every week, the shop where
          you're a regular — these conversations start warm. A business owner who already recognizes you is far more
          likely to say yes than a cold walk-in.
        </p>

        <h2 id="4-make-it-easy-to-say-yes-on-the-spot">4. Make it easy to say yes on the spot</h2>
        <p>
          Bring your fund page link — on a card, a flyer, or just pulled up on your phone — so a "yes" can turn into
          an actual gift right there, instead of a promise that gets forgotten once you leave.
        </p>

        <h2 id="5-follow-through-on-the-exchange">5. Follow through on the exchange</h2>
        <p>
          Once a business sponsors you, actually post the shoutout and add them to your supporter list — promptly,
          not "eventually." Businesses that had a good experience are the ones who sponsor again next season, and
          who tell other local business owners about you.
        </p>

        <h2 id="where-sport-fund-fits">Where Sport Fund fits</h2>
        <p>
          Your sponsorship link is what turns a business's "yes" into a completed gift. A Sport Fund page gives you
          one link built for that moment:
        </p>
        <ul>
          <li>
            <strong>You keep 100% of every donation</strong> — a $50 sponsorship reaches you as $50. The small
            platform fee (roughly 5–7%) is shown up front and added on top at checkout, never taken out of what a
            sponsor gives.
          </li>
          <li>
            <strong>One link works everywhere</strong> — in person, in an email, on a printed flyer — so a business
            can give in seconds instead of hunting for how to help.
          </li>
          <li>
            <strong>Payouts go straight to your family's bank account</strong> — no club, no middleman.
          </li>
        </ul>
        <p>
          <Link to="/signup-creator" className="text-blue-600">
            Start a free fund page
          </Link>{' '}
          to get your link before your next business ask.
        </p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 id="read-next">Read next</h2>
        <p>
          <Link to="/resources/fundraising-letter-template" className="text-blue-600">
            Sports Fundraising Letter Template: A Free Letter You Can Copy
          </Link>{' '}
          — the version built for family and friends.
        </p>
        <p>
          <Link to="/resources/how-to-fundraise-for-a-sports-season" className="text-blue-600">
            How to Fundraise for Your Sports Season: 7 Honest Ways That Actually Work
          </Link>{' '}
          — more ways to raise support beyond sponsorships.
        </p>
        <ArticleFeedback articleSlug="sponsorship-letter-template" />
      </article>
    </div>
  );
};

export default SponsorshipLetterTemplate;
